import { CheckBoxNoLabel } from "../../../components/CheckBoxes/CheckBoxes";
import { DatePicker } from "../../../components/DatePicker/DatePicker";
import {
  CheckBoxContainer,
  CheckBoxFinePrintLabel,
  CustomTermContainer,
  WarningMessage,
} from "../../../components/Form/Form";
import { SelectBoxV2 } from "../../../components/SelectBoxV2/SelectBoxV2";
import type { Fee, OptionType, UUID } from "../../../types/types";
import type {
  POCFormSchemaType,
  POCManualFormSchemaType,
} from "../../../util/util";
import {
  convertDeliveryTermToOption,
  convertPaymentModeToOption,
  convertPaymentTermToOption,
  markDefaultTerm,
  usePaymentMethods,
  usePaymentTerms,
  useShippingTerms,
  useStoreState,
} from "../../../util/util";
import { ColoredTextOnError } from "../../../util/util-components";
import { Controller, useFormContext } from "react-hook-form";
import type { ArrayField } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ConfirmationSpacer } from "../../public/SampleRequestCart/SampleRequestCart";
import type { RequestAddressSchemaType } from "../../public/RequestAddress/RequestAddress";
import { create_configured_checkboxes_fields } from "../../admin/SellerAdmin/SellerAdminSettings/TermsTab/ConfigureCheckboxsCard/utils";

export type CreateQuoteBottomFormOutput = RequestAddressSchemaType & {
  confirmation: boolean;
  is_tax_exempt: boolean;
  valid_to_date: string;
  required_eta: string;
  payment_method: OptionType<UUID>;
  feesList: Fee[];
  discount_code: string | null;
};

export interface CreateQuoteBottomFormPOCManual
  extends CreateQuoteBottomFormOutput,
    POCManualFormSchemaType {}

export interface CreateQuoteBottomFormPOCForm
  extends CreateQuoteBottomFormOutput,
    POCFormSchemaType {}

export function CreateSampleTermsForm({
  defaultPaymentMethod,
  isTaxExempt,
  updateTaxFee,
  defaultPaymentTerm,
  showAllFields = true,
  defaultDeliveryTerm,
  configuredCheckboxFields,
}: {
  defaultPaymentMethod: OptionType<UUID> | null;
  isTaxExempt: boolean;
  defaultDeliveryTerm: OptionType<UUID> | null;
  defaultPaymentTerm: OptionType<UUID> | null;
  showAllFields?: boolean;
  updateTaxFee?: (isTaxExempt: boolean) => void;
  configuredCheckboxFields?: Partial<ArrayField<Record<string, any>, "id">>[];
}) {
  const methodsOfUseForm = useFormContext();
  const { control, register, errors, formState, setError, watch } =
    methodsOfUseForm;
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();

  const { data: fetchedPaymentMethods, error: paymentMethodsError } =
    usePaymentMethods(storefront_id);

  const { data: fetchedPaymentTerms, error: paymentTermsError } =
    usePaymentTerms(storefront_id);

  const { data: fetchedDeliveryTerms, error: deliveryTermsError } =
    useShippingTerms(storefront_id);

  if (paymentMethodsError) {
    setError("payment_method", { message: "Failed to fetch payment methods" });
  }
  if (paymentTermsError) {
    setError("payment_terms", { message: "Failed to fetch payment terms" });
  }
  if (deliveryTermsError) {
    setError("delivery_term", { message: "Failed to fetch delivery terms" });
  }

  const watchedPaymentMethod = watch("payment_method");

  /**
   * This is compared by label because the ids are not stable between different objects.
   * @param defaultLabel
   * @param valueLabel
   * @returns
   */
  const isNotDefaultPaymentMethod = (
    defaultLabel: string | undefined,
    valueLabel: string
  ) => {
    if (!defaultLabel) {
      return false;
    } else if (valueLabel) {
      return !(defaultLabel === valueLabel);
    } else return false;
  };

  const paymentMethodsOptions =
    fetchedPaymentMethods?.data.map(convertPaymentModeToOption) ?? [];
  const paymentTermsOptions =
    fetchedPaymentTerms?.data.map(convertPaymentTermToOption) ?? [];
  const deliveryTermsOptions =
    fetchedDeliveryTerms?.data.map(convertDeliveryTermToOption) ?? [];
  const validToDate = watch("valid_to_date");
  const requiredEta = watch("required_eta");

  return (
    <>
      {showAllFields && (
        <CustomTermContainer
          highlight={
            !!defaultPaymentTerm &&
            !!watch("payment_term")?.value &&
            watch("payment_term")?.value !== defaultPaymentTerm?.value
          }
          has_error={!!errors.payment_term}
        >
          <Controller
            as={SelectBoxV2}
            control={control}
            name="payment_term"
            placeholder={t("Payment Terms")}
            options={markDefaultTerm(
              paymentTermsOptions,
              defaultPaymentTerm ?? undefined
            )}
            rules={{
              required: true,
            }}
            errors={errors}
            formState={formState}
            defaultValue={
              defaultPaymentTerm ?? methodsOfUseForm.getValues("payment_term")
            }
            setDefaultTerm={!!defaultPaymentTerm}
          />
          {!!defaultPaymentTerm &&
            !!watch("payment_term")?.value &&
            watch("payment_term")?.value !== defaultPaymentTerm?.value && (
              <WarningMessage>
                {t("Requested payment terms are not the default terms")}
              </WarningMessage>
            )}
        </CustomTermContainer>
      )}

      <CustomTermContainer
        highlight={isNotDefaultPaymentMethod(
          defaultPaymentMethod?.label,
          watchedPaymentMethod?.label
        )}
        has_error={!!errors.payment_method}
      >
        <Controller
          as={SelectBoxV2}
          control={control}
          name="payment_method"
          placeholder={t("Payment Method")}
          options={markDefaultTerm(
            paymentMethodsOptions,
            defaultPaymentMethod ?? undefined
          )}
          defaultValue={
            defaultPaymentMethod ?? { value: undefined, label: undefined }
          }
          rules={{ required: true }}
          errors={errors}
          formState={formState}
          setDefaultTerm={!!defaultPaymentMethod}
        />
        {isNotDefaultPaymentMethod(
          defaultPaymentMethod?.label,
          watchedPaymentMethod?.label
        ) && (
          <WarningMessage>
            {t("Requested payment method is not the default")}
          </WarningMessage>
        )}
      </CustomTermContainer>

      {showAllFields && (
        <CustomTermContainer
          highlight={
            !!defaultDeliveryTerm &&
            !!watch("delivery_term")?.value &&
            watch("delivery_term")?.value !== defaultDeliveryTerm?.value
          }
          has_error={!!errors.delivery_term}
        >
          <Controller
            as={SelectBoxV2}
            control={control}
            name="delivery_term"
            placeholder={t("Shipping Terms")}
            options={markDefaultTerm(
              deliveryTermsOptions,
              defaultDeliveryTerm ?? undefined
            )}
            rules={{
              required: true,
            }}
            errors={errors}
            defaultValue={
              defaultDeliveryTerm ?? methodsOfUseForm.getValues("delivery_term")
            }
            formState={formState}
            setDefaultTerm={!!defaultDeliveryTerm}
          />
          {!!defaultDeliveryTerm &&
            !!watch("delivery_term")?.value &&
            watch("delivery_term")?.value !== defaultDeliveryTerm?.value && (
              <WarningMessage>
                {t("Requested shipping terms are not the default terms")}
              </WarningMessage>
            )}
        </CustomTermContainer>
      )}

      <DatePicker
        label={t("Price valid until")}
        name={"valid_to_date"}
        methodsOfUseForm={methodsOfUseForm}
        required={true}
        defaultValue={validToDate}
      />

      <DatePicker
        label={t("Shipment ETA")}
        name={"required_eta"}
        methodsOfUseForm={methodsOfUseForm}
        required={true}
        defaultValue={requiredEta}
      />

      {updateTaxFee && (
        <ConfirmationSpacer>
          <CheckBoxContainer style={{ marginBottom: "15px", width: "100%" }}>
            <div style={{ width: "22px", marginRight: "15px" }}>
              <CheckBoxNoLabel
                name="is_tax_exempt"
                id="is_tax_exempt"
                defaultChecked={isTaxExempt}
                onChange={(event) => updateTaxFee(event.currentTarget.checked)}
                ref={register({ required: false })}
              />
            </div>
            <CheckBoxFinePrintLabel htmlFor="is_tax_exempt">
              <ColoredTextOnError isError={!!errors.is_tax_exempt?.message}>
                {t("Tax Exempt")}
              </ColoredTextOnError>
            </CheckBoxFinePrintLabel>
          </CheckBoxContainer>
        </ConfirmationSpacer>
      )}
      <ConfirmationSpacer>
        {configuredCheckboxFields && configuredCheckboxFields?.length > 0 && (
          <>
            {create_configured_checkboxes_fields({
              fields: configuredCheckboxFields,
              methodsOfUseForm,
              t,
            })}
          </>
        )}
      </ConfirmationSpacer>
    </>
  );
}
